// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ChatInput, type ChatInputProps} from '@github-ui/copilot-chat/components/ChatInput'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {queryForRepos, RepoBranchAgentPicker} from '@github-ui/copilot-chat/components/RepoBranchAgentSelector'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {CopilotChatRepo, LineRange} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useCallback, useEffect, useState} from 'react'
import {AgentIcon} from '@primer/octicons-react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {Repository} from '@github-ui/current-repository'
import {useSearchParams} from '@github-ui/react-router'
import {MemberRequestBanner} from './MemberRequestBanner'
import type {DropdownRepository} from '../../hooks/useCopilotRepoFetching'

import {
  makeFileReference,
  makeSnippetReference,
  referenceName as getReferenceName,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'

export type CreateTaskReference = {
  path: string
  range?: LineRange
}

function makeFileOrSnippetReference({
  referenceRepo,
  reference,
}: {
  referenceRepo: CopilotChatRepo
  reference: CreateTaskReference
}) {
  if (reference.range) {
    return makeSnippetReference(reference.path, reference.range, referenceRepo)
  } else {
    return makeFileReference(reference.path, referenceRepo)
  }
}

export type NewTaskChatInputProps = {
  handleUserSubmit: (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedSubAgent: string,
    fileReference?: string,
  ) => Promise<void>
  initialRepo?: Repository | null
  initialReference?: CreateTaskReference | null
  hideRepoSelector?: boolean
  isInPortal?: ChatInputProps['isInPortal']
  textAreaRef?: ChatInputProps['textAreaRef']
}

export function NewTaskChatInput({
  handleUserSubmit,
  initialRepo,
  initialReference,
  hideRepoSelector,
  isInPortal,
  textAreaRef,
}: NewTaskChatInputProps) {
  // Use the chat manager to clear references when a new task is started
  const manager = useChatManager()
  const [selectedRepo, setSelectedRepo] = useState<DropdownRepository | null>(null)
  const [ccaDisabled, setCCADisabled] = useState(false)
  const isMemberRequestAgentPanelEnabled = isFeatureEnabled('swe_agent_member_requests_agent_panel')
  const [referenceRepo, setReferenceRepo] = useState<CopilotChatRepo | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<string>('')
  const [selectedSubAgent, setSelectedSubAgent] = useState<string | null>(null)
  const [reference, setReference] = useState<CreateTaskReference | null>(initialReference || null)
  const [searchParams] = useSearchParams()
  const branchParam = searchParams.get('branch')

  const isSubAgentsEnabled = isFeatureEnabled('copilot_swe_agent_use_subagents')
  const isValidRepo = initialRepo && Object.keys(initialRepo).length > 0
  const nwoParam = searchParams.get('repo')
  let initialNameWithOwner = ''
  if (isValidRepo) {
    initialNameWithOwner = `${initialRepo.ownerLogin}/${initialRepo.name}`
  } else if (nwoParam) {
    initialNameWithOwner = nwoParam
  }

  const onSubmit = useCallback(
    async (content: string) => {
      const referenceName =
        referenceRepo && reference
          ? getReferenceName(makeFileOrSnippetReference({referenceRepo, reference}))
          : undefined
      return handleUserSubmit(content, selectedRepo, selectedBranch, selectedSubAgent || '', referenceName)
    },
    [handleUserSubmit, selectedRepo, selectedBranch, selectedSubAgent, reference, referenceRepo],
  )

  // Fetch the reference repo
  useEffect(() => {
    if (initialRepo?.id != null) {
      ;(async () => {
        const repoResponse = await manager.service.fetchRepo(initialRepo.id)
        if (repoResponse.payload) setReferenceRepo(repoResponse.payload)
      })()
    }
  }, [initialRepo, manager])

  useEffect(() => {
    // If the repo selector is hidden and we have an initial repo NWO, try to auto-select it.
    if (hideRepoSelector && initialNameWithOwner) {
      const fetchInitialRepo = async () => {
        try {
          const repositories = await queryForRepos(initialNameWithOwner)
          const repo = repositories.find(
            (repoData: {nameWithOwner: string}) => repoData.nameWithOwner === initialNameWithOwner,
          )

          if (repo && repo.enabled) {
            setSelectedRepo(repo)
            setSelectedBranch(branchParam ?? repo.defaultBranch)
            setCCADisabled(false)
            return
          }
          if (repo && isMemberRequestAgentPanelEnabled) {
            setCCADisabled(true)
          }
        } catch {
          // Error querying for specific repo
        }
      }
      fetchInitialRepo()
    }
    // We only want this hook to run once since it's fetching the initial repo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <ChatInput
        modes={[
          {
            name: 'Agent',
            icon: <AgentIcon />,
            submitLabel: 'Start task',
            placeholder: 'Describe a coding task to work on',
            textAreaAriaLabel: 'Coding task prompt',
            supportsAttachments: false,
            // Setting this to false because the references aren't properly included in the problem statement sent to CCA.
            supportsReferenceAttachments: false,
          },
        ]}
        size="large"
        onSubmit={onSubmit}
        initialReference={
          referenceRepo && reference ? makeFileOrSnippetReference({reference, referenceRepo}) : undefined
        }
        extraToolbar={
          <RepoBranchAgentPicker
            hideRepoSelector={hideRepoSelector}
            initialRepoNWO={initialNameWithOwner}
            initialBranch={branchParam || undefined}
            selectedRepo={selectedRepo || undefined}
            selectedBranch={selectedBranch}
            selectedCustomAgent={selectedSubAgent || undefined}
            onRepoSelect={(repo: DropdownRepository | undefined) => {
              if (!repo) return
              setSelectedRepo(repo)
              setSelectedBranch(repo.defaultBranch || '')
              setCCADisabled(!repo.enabled && isMemberRequestAgentPanelEnabled)
            }}
            onBranchSelect={(branch: string | undefined) => setSelectedBranch(branch || '')}
            onCustomAgentSelect={(agent: string | undefined) => setSelectedSubAgent(agent || null)}
            agentSelectionEnabled={isSubAgentsEnabled && !ccaDisabled}
          />
        }
        submitDisabled={!selectedRepo || ccaDisabled}
        disableSavedUserMessage
        onRemoveReference={() => {
          setReference(null)
        }}
        isInPortal={isInPortal}
        textAreaRef={textAreaRef}
      />
      {ccaDisabled && selectedRepo && isMemberRequestAgentPanelEnabled && (
        <MemberRequestBanner
          repo={selectedRepo}
          onRepoUpdate={async () => {
            // Re-fetch the repository data to get updated CCA flags
            const nameWithOwner = selectedRepo.nameWithOwner
            const repos = await queryForRepos(nameWithOwner)
            const updatedRepo = repos.find(r => r.nameWithOwner === nameWithOwner)
            if (updatedRepo) {
              setSelectedRepo(updatedRepo)
              setCCADisabled(!updatedRepo.enabled && isMemberRequestAgentPanelEnabled)
            }
          }}
        />
      )}
    </>
  )
}

try{ NewTaskChatInput.displayName ||= 'NewTaskChatInput' } catch {}